import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useSelector } from "react-redux";
import Backdrop from "../components/common/Backdrop";
import Image from "../components/common/Image";
import { useGetVipRankDetailsQuery } from "../features/api/publicApiSlice";
import { useGetProfileQuery } from "../features/api/userApiSlice";
import { useTranslation } from "react-i18next";
import { selectCurrentToken } from "../features/slices/authSlice";

import VipCard from "../components/vip/VipCard"; 
import VipProgress from "../components/vip/VipProgress"; 
import VipHeaderSummary from "../components/vip/VipHeaderSummary";
import VipPass from "../components/vip/VipPass";
import Vip from "./Vip";

// --- MAIN COMPONENT ---
const Vip2 = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("md"));
  const lang = localStorage.getItem("language");
  const isLoggedIn = useSelector(selectCurrentToken);

  const [selectedRankIndex, setSelectedRankIndex] = useState(0); 
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0); 
  
  const mobileScrollRef = useRef(null);

  // 1. Fetch VIP Tables
  const {
    data: vipRankDetails,
    isFetching: isVipRankDetailsFetching,
    isLoading: isVipRankDetailsLoading,
    isSuccess: isVipRankDetailsSuccess,
  } = useGetVipRankDetailsQuery({ prefetch: true });

  // 2. Fetch User Profile
  const { data: userData } = useGetProfileQuery(undefined, { 
    skip: !isLoggedIn 
  });

  const vipRanks = vipRankDetails?.result?.vipRanks || [];
  const userCurrentDeposit = userData?.result?.accumulatedDeposit || 0;
  const userRank = userData?.result?.playerRank || "VIP0";

  // Calculate actual User Rank Index
  let activeRankIndex = 0;
  if (vipRanks.length > 0) {
      if (userRank) {
        const rankNum = parseInt(userRank.replace(/VIP/i, '') || "0");
        const foundIndex = vipRanks.findIndex(r => parseInt(r.vipRank) === rankNum);
        if(foundIndex !== -1) activeRankIndex = foundIndex;
      }
  }

  // Fallback to old page
  if (!isVipRankDetailsLoading && !isVipRankDetailsFetching) {
    if (!vipRanks || vipRanks.length === 0) {
      return <Vip />;
    }
  }

  const getMappedTier = (rank) => ({
    rank: rank.vipRank,
    displayName: rank.customDisplayName,
    logo: rank.logo,
    accumulatedDeposit: rank.accumulatedDeposit || 0,
    upgradeBonus: rank.vipUpgradeBonus || 0,
    attributes: {
      slotsRebatePercentage: rank.slotsRebatePercentage || 0,
      sportsRebatePercentage: rank.sportsRebatePercentage || 0,
      liveCasinoRebatePercentage: rank.liveCasinoRebatePercentage || 0,
    },
  });

  const getStepWidth = () => {
    const container = mobileScrollRef.current;
    if (!container) return 0;
    const firstCard = container.children[0];
    // Card width + gap (theme spacing 2 = 16px)
    return firstCard ? firstCard.offsetWidth + 16 : container.offsetWidth;
  };

  const handleMobileScroll = () => {
    const container = mobileScrollRef.current;
    if (!container) return;
    const step = getStepWidth();
    if (!step) return;
    const index = Math.round(container.scrollLeft / step);
    setMobileActiveIndex(index);
  };

  const scrollToIndex = (index) => {
    const container = mobileScrollRef.current;
    if (!container) return;
    const step = getStepWidth();
    container.scrollTo({ left: index * step, behavior: "smooth" });
  };

  return (
    <>
      {(isVipRankDetailsLoading || isVipRankDetailsFetching) && <Backdrop />}

      <Box
        sx={{
          pt: mobileView ? "55px" : "0",
          backgroundColor: theme.palette.background.home || "#0f172a",
          minHeight: "100vh",
          pb: 8,
        }}
      >
        {/* Banner */}
        {(!mobileView || process.env.REACT_APP_VIP_BANNER_MOBILE !== "false") && (
          <Image
            src={
              lang === "en" || lang === null
                ? process.env.REACT_APP_VIP_BANNER_EN
                : process.env.REACT_APP_VIP_BANNER_BD
            }
          />
        )}

        {/* Content */}
        {process.env.REACT_APP_VIP_IMG_CONTENT === "true" ? (
          !mobileView ? (
            <Image src={lang === "en" || lang === null ? process.env.REACT_APP_VIP_CONTENT_EN : process.env.REACT_APP_VIP_CONTENT_BD} />
          ) : (
            <Box sx={{ width: "100%", overflow: "auto", display: "flex" }}>
              <Image sx={{ minWidth: "200%", height: "auto" }} src={process.env.REACT_APP_VIP_MOBILE_CONTENT_EN} />
            </Box>
          )
        ) : (
          <Container maxWidth="xl" sx={{ px: mobileView ? 0 : 3 }}>
            <Box py={{ xs: 3, sm: 5 }}>
          
              <Box mb={5} textAlign="center" px={2}>
                <Typography variant="h4" component="h2" sx={{ fontWeight: "bold", color: "white", mb: 1 }}>
                  {t("home.vip.title") || "VIP Privileges"}
                </Typography>
                <Typography variant="body1" sx={{ color: "#94a3b8" }}>
                      {t("vipnew.levelup")}
                </Typography>
              </Box>

              {isVipRankDetailsSuccess && vipRanks.length > 0 ? (
                <>
                  {/* ================= MOBILE VIEW ================= */}
                  {mobileView ? (
                    <Box>
                      {isLoggedIn && (
                         <Box px={2} mb={4}>
                            <VipProgress ranks={vipRanks} currentDeposit={userCurrentDeposit} userRank={userRank} />
                         </Box>
                      )}
                      
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mb: 3,
                        }}
                      >
                        <IconButton
                          aria-label="previous VIP tier"
                          onClick={() => scrollToIndex(mobileActiveIndex - 1)}
                          sx={{
                            flexShrink: 0,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                            visibility: mobileActiveIndex > 0 ? "visible" : "hidden",
                          }}
                        >
                          <ChevronLeft />
                        </IconButton>

                        <Box
                          ref={mobileScrollRef}
                          onScroll={handleMobileScroll}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            overflowX: "auto",
                            scrollSnapType: "x mandatory",
                            gap: 2,
                            pb: 1,
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollBehavior: "smooth",
                          }}
                        >
                          {vipRanks.map((rank, index) => (
                            <Box
                              key={rank.id || index}
                              sx={{
                                flex: "0 0 100%",
                                minWidth: "100%",
                                scrollSnapAlign: "center",
                                position: 'relative',
                              }}
                            >
                               <Box sx={{ height: '100%' }}>
                                  <VipCard tier={getMappedTier(rank)} />
                               </Box>
                            </Box>
                          ))}
                        </Box>

                        <IconButton
                          aria-label="next VIP tier"
                          onClick={() => scrollToIndex(mobileActiveIndex + 1)}
                          sx={{
                            flexShrink: 0,
                            bgcolor: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                            visibility:
                              mobileActiveIndex < vipRanks.length - 1
                                ? "visible"
                                : "hidden",
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    /* ================= DESKTOP VIEW ================= */
                     <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', // Centers children horizontally
                        width: '100%' 
                      }}
                    >
                      {/* 1. Header Summary (Centered & Constrained Width) */}
                      {isLoggedIn && (
                        <Box sx={{ width: '100%', maxWidth: 700, mb: 4 }}>
                          <VipHeaderSummary 
                            ranks={vipRanks} 
                            currentDeposit={userCurrentDeposit} 
                            userRank={userRank} 
                          />
                        </Box>
                      )}

                      {/* 2. Battle Pass Table (Centered & Constrained Width) */}
                      {/* maxWidth="lg" ensures it doesn't touch the very edges on huge screens */}
                      <Box sx={{ width: 'auto', maxWidth: '100%', px: 2 }}> 
                        <VipPass 
                          ranks={vipRanks}
                          activeRankIndex={activeRankIndex}
                          selectedIndex={selectedRankIndex}
                          onSelect={setSelectedRankIndex}
                        />
                      </Box>
                      
                    </Box>
                  )}
                </>
              ) : (
                !isVipRankDetailsLoading && (
                  <Box display="flex" justifyContent="center" color="white">
                    <Typography>No VIP data available.</Typography>
                  </Box>
                )
              )}
            </Box>
          </Container>
        )}
      </Box>
    </>
  );
};

export default Vip2;